import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

// Thunk for fetching categories
export const get_category = createAsyncThunk(
    'product/get_category',
    async (_, { fulfillWithValue }) => {
        try {
            const { data } = await api.get('/home/get-categorys');
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

export const fetchSellers = createAsyncThunk(
    "home/fetchSellers",
    async ({ sortPrice, pageNumber } = {}, { rejectWithValue }) => {
        try {
            const verified = sortPrice === "verified";
            const featured = sortPrice === "featured";
            const { data } = await api.get(
                `/home/sellers?verified=${verified}&featured=${featured}&page=${pageNumber}`
            );
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Error fetching sellers");
        }
    }
);

export const fetchFeaturedSellers = createAsyncThunk(
    "home/fetchFeaturedSellers",
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get("/home/featured-sellers");
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Error fetching featured sellers");
        }
    }
);



// Thunk for fetching products
export const get_products = createAsyncThunk(
    'product/get_products',
    async (_, { fulfillWithValue }) => {
        try {
            const { data } = await api.get('/home/get-products');
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

export const get_product = createAsyncThunk(
    'product/get_product',
    async (slug, { fulfillWithValue }) => {
        try {
            const { data } = await api.get(`/home/get-product/${slug}`);
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

// Thunk for fetching products in a price range
export const price_range_product = createAsyncThunk(
    'product/price_range_product',
    async (_, { fulfillWithValue }) => {
        try {
            const { data } = await api.get('/home/price-range-latest-product');
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

// Thunk for fetching banners
export const get_banners = createAsyncThunk(
    'product/get_banners',
    async (_, { fulfillWithValue }) => {
        try {
            const { data } = await api.get('/banners');
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

// Thunk for fetching platform banners
export const get_platform_banners = createAsyncThunk(
    'product/get_platform_banners',
    async (_, { fulfillWithValue }) => {
        try {
            const { data } = await api.get('/platform/banners');
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

// Thunk for querying products
export const query_products = createAsyncThunk(
    'product/query_products',
    async (query, { fulfillWithValue }) => {
        try {
            const { data } = await api.get(
                `/home/query-products?category=${encodeURIComponent(query.category)}&&rating=${query.rating}&&lowPrice=${query.low}&&highPrice=${query.high}&&sortPrice=${query.sortPrice}&&pageNumber=${query.pageNumber}&&searchValue=${query.searchValue ? encodeURIComponent(query.searchValue) : ''}`
            );
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

export const fetchProductsBySeller = createAsyncThunk(
    "home/fetchProductsBySeller",
    async ({ sellerId, pageNumber }, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/home/get-products-by-seller?sellerId=${sellerId}&page=${pageNumber}`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Error fetching products by seller");
        }
    }
);

export const fetchStoreBySlug = createAsyncThunk(
    "home/fetchStoreBySlug",
    async (slug, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/home/get-store-by-slug/${slug}`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Error fetching store by slug");
        }
    }
);

export const fetchStoreByDomain = createAsyncThunk(
    "home/fetchStoreByDomain",
    async (domain, { rejectWithValue }) => {
        try {
            const { data } = await api.get(`/home/get-store-by-domain/${domain}`);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Error fetching store by domain");
        }
    }
);


// Thunk for submitting customer reviews
export const customer_review = createAsyncThunk(
    'review/customer_review',
    async (info, { fulfillWithValue }) => {
        try {
            const { data } = await api.post('/home/customer/submit-review', info);
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

// Thunk for fetching reviews
export const get_reviews = createAsyncThunk(
    'review/get_reviews',
    async ({ productId, pageNumber }, { fulfillWithValue }) => {
        try {
            const { data } = await api.get(
                `/home/customer/get-reviews/${productId}?pageNo=${pageNumber}`
            );
            return fulfillWithValue(data);
        } catch (error) {
            console.log(error.response);
        }
    }
);

// Home reducer
export const homeReducer = createSlice({
    name: 'home',
    initialState: {
        categorys: [],
        products: [],
        sellers: [],
        featuredSellers: [],
        totalSellers: 0,
        parPage: 4,
        seller: {},
        totalProducts: 0, // Total products for seller-specific fetch
        latest_product: [],
        topRated_product: [],
        discount_product: [],
        priceRange: {
            low: 0,
            high: 100,
        },
        product: {},
        relatedProducts: [],
        moreProducts: [],
        successMessage: '',
        errorMessage: '',
        totalReview: 0,
        rating_review: [],
        loading: false,
        reviews: [],
        banners: [],
        platformBanners: [],
        storeSearchKeyword: '',
        storeCategoryFilter: ''
    },
    reducers: {
        messageClear: (state) => {
            state.successMessage = '';
            state.errorMessage = '';
        },
        setStoreSearchKeyword: (state, { payload }) => {
            state.storeSearchKeyword = payload;
        },
        setStoreCategoryFilter: (state, { payload }) => {
            state.storeCategoryFilter = payload;
        }
    },
    extraReducers: {

        [get_category.fulfilled]: (state, { payload }) => {
            state.categorys = payload.categorys;
        },

        [fetchSellers.pending]: (state) => {
            state.errorMessage = '';
        },
        [fetchSellers.fulfilled]: (state, { payload }) => {
            state.sellers = payload.sellers;
            state.totalSellers = payload.totalSellers;
        },
        [fetchSellers.rejected]: (state, { payload }) => {
            state.errorMessage = payload || 'Failed to fetch sellers';
        },

        [fetchFeaturedSellers.pending]: (state) => {
            state.errorMessage = '';
        },
        [fetchFeaturedSellers.fulfilled]: (state, { payload }) => {
            state.featuredSellers = payload.sellers;
        },
        [fetchFeaturedSellers.rejected]: (state, { payload }) => {
            state.errorMessage = payload || 'Failed to fetch featured sellers';
        },
        // Handle get_products
        [get_products.fulfilled]: (state, { payload }) => {
            state.products = payload.products;
            state.latest_product = payload.latest_product;
            state.topRated_product = payload.topRated_product;
            state.discount_product = payload.discount_product;
        },
        // Handle get_product
        [get_product.pending]: (state) => {
            state.loading = true;
            state.errorMessage = '';
            state.product = {};
            state.relatedProducts = [];
            state.moreProducts = [];
        },
        [get_product.fulfilled]: (state, { payload }) => {
            state.loading = false;
            if (payload) {
                state.product = payload.product || {};
                state.relatedProducts = payload.relatedProducts || [];
                state.moreProducts = payload.moreProducts || [];
            }
            state.errorMessage = '';
        },
        [get_product.rejected]: (state, action) => {
            state.loading = false;
            state.product = {};
            state.relatedProducts = [];
            state.moreProducts = [];
            state.errorMessage = action.error.message || 'Failed to fetch product';
        },

        // Handle price_range_product
        [price_range_product.fulfilled]: (state, { payload }) => {
            state.latest_product = payload.latest_product;
            state.priceRange = payload.priceRange;
        },
        // Handle query_products
        [query_products.pending]: (state) => {
            state.loading = true;
        },
        [query_products.fulfilled]: (state, { payload }) => {
            state.loading = false;
            state.products = payload.products;
            state.totalProduct = payload.totalProduct;
            state.parPage = payload.parPage;
        },
        [query_products.rejected]: (state) => {
            state.loading = false;
        },
        // Handle customer_review
        [customer_review.fulfilled]: (state, { payload }) => {
            state.successMessage = payload.message;
        },
        // Handle get_reviews
        [get_reviews.fulfilled]: (state, { payload }) => {
            state.reviews = payload.reviews;
            state.totalReview = payload.totalReview;
            state.rating_review = payload.rating_review;
        },
        // Handle get_banners
        [get_banners.fulfilled]: (state, { payload }) => {
            state.banners = payload.banners;
        },
        // Handle get_platform_banners
        [get_platform_banners.fulfilled]: (state, { payload }) => {
            state.platformBanners = payload.banners;
        },
        // Fetch products by seller
        [fetchProductsBySeller.pending]: (state) => {
            state.errorMessage = '';
            state.loading = true;
        },
        [fetchProductsBySeller.fulfilled]: (state, { payload }) => {
            console.log("Payload received for seller:", payload.seller);
            state.loading = false;
            state.seller = payload.seller || {};
            state.products = payload.products;
            state.totalProducts = payload.totalProducts;
        },
        [fetchProductsBySeller.rejected]: (state, { payload }) => {
            state.loading = false;
            state.errorMessage = payload || "Failed to fetch products by seller";
        },
        [fetchStoreBySlug.pending]: (state) => {
            state.errorMessage = '';
            state.loading = true;
        },
        [fetchStoreBySlug.fulfilled]: (state, { payload }) => {
            state.loading = false;
            state.seller = payload.seller || {};
            state.products = payload.products;
            state.totalProducts = payload.totalProducts;
        },
        [fetchStoreBySlug.rejected]: (state, { payload }) => {
            state.loading = false;
            state.errorMessage = payload || "Failed to fetch store by slug";
        },
        [fetchStoreByDomain.pending]: (state) => {
            state.errorMessage = '';
            state.loading = true;
        },
        [fetchStoreByDomain.fulfilled]: (state, { payload }) => {
            state.loading = false;
            state.seller = payload.seller || {};
            state.products = payload.products;
            state.totalProducts = payload.totalProducts;
        },
        [fetchStoreByDomain.rejected]: (state, { payload }) => {
            state.loading = false;
            state.errorMessage = payload || "Failed to fetch store by domain";
        },
    },
});


export const { messageClear, setStoreSearchKeyword, setStoreCategoryFilter } = homeReducer.actions;
export default homeReducer.reducer;
